import { useCallback, useEffect, useRef, useState } from 'react';

import { IDLE_TIMEOUT_MINUTES_FC } from '@/constants';

interface UseIdleTimerReturn {
  isIdle: boolean;
  resetIdle: () => void;
}

/**
 * Hook para detectar inactividad del usuario en la ventana del Fast Checkout.
 * Detecta eventos globales de interacción (mouse, teclado, scroll, etc.)
 *
 * @returns { isIdle, resetIdle }   Estado de inactividad y función para resetearlo manualmente
 */
export const useIdleTimer = (): UseIdleTimerReturn => {
  const [isIdle, setIsIdle] = useState(false);
  const timeout = IDLE_TIMEOUT_MINUTES_FC * 60 * 1000 || 10 * 60 * 1000;
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setIsIdle(true);
    }, timeout);
  }, [timeout]);

  const resetIdle = useCallback(() => {
    setIsIdle(false);
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    const handleActivity = () => {
      if (isIdle) return; // si ya está inactivo, no reiniciamos nada automáticamente
      startTimer();
    };

    const events = [
      'mousemove',
      'keydown',
      'mousedown',
      'scroll',
      'touchstart'
    ];
    events.forEach((event) => window.addEventListener(event, handleActivity));

    startTimer();

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isIdle, startTimer]);

  return { isIdle, resetIdle };
};
